import React from 'react';
import {
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { BORDER, INK, RADIUS, TYPE } from '../foundation';
import { DS2_SPACE } from './tokens';
import { EnsInline, EnsLoader, EnsSkeleton } from '../../components/common/EnsLoader';

type SxObject = Record<string, unknown>;
const asSx = (sx: SxProps<Theme> | undefined): SxObject => (sx ?? {}) as SxObject;

/* ── LoadingState ──────────────────────────────────────────────────── */

export interface LoadingStateProps {
  label?: string;
  rows?: number;
  /**
   * `spinner` — centered block (page / panel body)
   * `inline` — small spinner + text on one line (inside a sentence or toolbar)
   * `skeleton` — placeholder rows (lists, tables)
   * `metrics` — placeholder KPI tiles
   */
  variant?: 'spinner' | 'inline' | 'skeleton' | 'metrics';
  sx?: SxProps<Theme>;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  label = 'Loading…',
  rows,
  variant = 'skeleton',
  sx,
}) => {
  if (variant === 'inline') {
    return (
      <Box sx={{ py: 0.5, ...asSx(sx) }}>
        <EnsInline label={label && <Typography component="span" sx={{ ...TYPE.caption, color: 'text.secondary' }}>{label}</Typography>} />
      </Box>
    );
  }

  if (variant === 'skeleton' || variant === 'metrics') {
    const n = rows ?? 4;
    return (
      <Box sx={sx} role="status" aria-live="polite" aria-busy="true">
        {label && <span className="ens-sr">{label}</span>}
        {variant === 'metrics' ? (
          <div className="ens-skel-tiles">
            {Array.from({ length: n }, (_, i) => (
              <div className="ens-skel-tile" key={i}>
                <EnsSkeleton width="45%" height={10} />
                <EnsSkeleton width="65%" height={22} />
                <EnsSkeleton width="35%" height={10} />
              </div>
            ))}
          </div>
        ) : (
          <div className="ens-skel-list">
            {Array.from({ length: n }, (_, i) => (
              <div className="ens-skel-item" key={i}>
                <EnsSkeleton shape="block" width={40} height={40} />
                <span className="ens-skel-lines">
                  <EnsSkeleton width={`${70 - (i % 3) * 6}%`} />
                  <EnsSkeleton width={`${45 - (i % 3) * 4}%`} height={10} />
                </span>
                <EnsSkeleton shape="round" width={64} height={24} />
              </div>
            ))}
          </div>
        )}
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <EnsLoader label={label || 'Loading…'} />
    </Box>
  );
};

/* ── EmptyState ────────────────────────────────────────────────────── */

export interface EmptyStateProps {
  title: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  /** Smaller padding for use inside a panel */
  compact?: boolean;
  sx?: SxProps<Theme>;
}

/** Nothing to show yet — say why, and what to do next. */
export const EmptyState: React.FC<EmptyStateProps> = ({ title, description, icon, action, compact, sx }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      px: compact ? 2 : 3,
      py: compact ? 3 : 6,
      border: `1px dashed ${BORDER.default}`,
      borderRadius: `${RADIUS.card}px`,
      bgcolor: 'transparent',
      ...asSx(sx),
    }}
  >
    {icon && (
      <Box
        aria-hidden
        sx={{
          color: INK.tertiary,
          mb: 1.5,
          display: 'flex',
          '& svg': { fontSize: compact ? 28 : 36, width: compact ? 28 : 36, height: compact ? 28 : 36 },
        }}
      >
        {icon}
      </Box>
    )}
    <Typography component="div" sx={{ ...(compact ? TYPE.subheading : TYPE.sectionTitle), color: 'text.primary' }}>
      {title}
    </Typography>
    {description && (
      <Typography component="div" sx={{ ...TYPE.bodySm, color: 'text.secondary', mt: 0.5, maxWidth: 440 }}>
        {description}
      </Typography>
    )}
    {action && <Box sx={{ mt: DS2_SPACE.md }}>{action}</Box>}
  </Box>
);

/* ── ErrorState ────────────────────────────────────────────────────── */

export interface ErrorStateProps {
  title?: string;
  /** A human sentence; pass the caught error to `error` to derive one */
  message?: React.ReactNode;
  error?: unknown;
  onRetry?: () => void;
  retryLabel?: string;
  sx?: SxProps<Theme>;
}

function errorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') return error;
  if (error instanceof Error && error.message) return error.message;
  const maybe = error as { message?: unknown };
  return typeof maybe.message === 'string' ? maybe.message : undefined;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Couldn’t load this',
  message,
  error,
  onRetry,
  retryLabel = 'Try again',
  sx,
}) => {
  const [retrying, setRetrying] = React.useState(false);
  const handleRetry = () => {
    if (!onRetry) return;
    const result = onRetry() as unknown;
    if (result && typeof (result as Promise<unknown>).finally === 'function') {
      setRetrying(true);
      void (result as Promise<unknown>).finally(() => setRetrying(false));
    }
  };

  return (
    <Box
      role="alert"
      sx={[
        (theme) => {
          const t = theme.palette.error.main;
          return {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            width: '100%',
            p: '16px 16px 16px 18px',
            borderRadius: '16px',
            border: `1px solid ${alpha(t, 0.32)}`,
            background: `radial-gradient(420px 120px at 0% 0%, ${alpha(t, 0.1)}, transparent 75%), ${theme.palette.background.paper}`,
            backdropFilter: 'blur(22px)',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 14,
              bottom: 14,
              width: 3,
              borderRadius: '0 3px 3px 0',
              bgcolor: t,
            },
            '@media (max-width:560px)': {
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              '& .ens-err-txt': { flexBasis: 'calc(100% - 52px)' },
              '& .ens-err-btn': { ml: '52px' },
            },
          };
        },
        asSx(sx),
      ]}
    >
      <Box
        aria-hidden
        sx={(theme) => ({
          flex: 'none',
          width: 38,
          height: 38,
          borderRadius: '11px',
          display: 'grid',
          placeItems: 'center',
          color: theme.palette.error.main,
          bgcolor: alpha(theme.palette.error.main, 0.12),
          border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
        })}
      >
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5v5.5M12 16.5h.01" />
        </svg>
      </Box>
      <Box className="ens-err-txt" sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="h2" sx={{ m: 0, fontSize: 15, fontWeight: 650, letterSpacing: '-0.02em', lineHeight: 1.35, color: 'text.primary' }}>
          {title}
        </Typography>
        <Typography component="div" sx={{ mt: '2px', fontSize: 13.5, color: 'text.secondary' }}>
          {message ?? errorMessage(error) ?? 'Something went wrong. Check your connection and try again.'}
        </Typography>
      </Box>
      {onRetry && (
        <Box
          component="button"
          type="button"
          className="ens-err-btn"
          onClick={handleRetry}
          disabled={retrying}
          sx={(theme) => {
            const t = theme.palette.error.main;
            return {
              flex: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              height: 36,
              px: '14px',
              borderRadius: '11px',
              border: `1px solid ${alpha(t, 0.35)}`,
              background: `radial-gradient(120px 60px at 0% 0%, ${alpha(t, 0.18)}, transparent 80%), ${alpha(theme.palette.text.primary, 0.035)}`,
              color: 'text.primary',
              font: 'inherit',
              fontSize: 13.5,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              transition: 'border-color .2s, box-shadow .2s',
              '& svg': { color: t },
              '&:hover': { borderColor: alpha(t, 0.65), boxShadow: `0 8px 24px -12px ${alpha(t, 0.8)}` },
              '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
              '&:disabled': { cursor: 'progress', opacity: 0.75 },
              '&:disabled svg': { animation: 'ens-err-spin .8s linear infinite' },
              '@keyframes ens-err-spin': { to: { transform: 'rotate(360deg)' } },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:disabled svg': { animation: 'none' } },
            };
          }}
        >
          <svg aria-hidden width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 12a8 8 0 1 1-2.34-5.66" />
            <path d="M20 4v5h-5" />
          </svg>
          <span>{retrying ? 'Retrying…' : retryLabel}</span>
        </Box>
      )}
    </Box>
  );
};

/* ── DataState ─────────────────────────────────────────────────────── */

export interface DataStateProps {
  isLoading?: boolean;
  error?: unknown;
  /** True when loaded successfully but there is nothing to show */
  isEmpty?: boolean;
  loading?: React.ReactNode;
  empty?: React.ReactNode;
  errorTitle?: string;
  onRetry?: () => void;
  children: React.ReactNode;
}

/**
 * Renders exactly one of: error → loading → empty → content.
 * Use it wherever a query drives a view so a failed request can never
 * masquerade as "nothing here yet", and loading never flashes an empty state.
 */
export const DataState: React.FC<DataStateProps> = ({
  isLoading,
  error,
  isEmpty,
  loading,
  empty,
  errorTitle,
  onRetry,
  children,
}) => {
  if (error) return <ErrorState title={errorTitle} error={error} onRetry={onRetry} />;
  if (isLoading) return <>{loading ?? <LoadingState variant="spinner" />}</>;
  if (isEmpty) return <>{empty ?? <EmptyState title="Nothing here yet" compact />}</>;
  return <>{children}</>;
};
